import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { ArrowRightStartOnRectangleIcon, Bars4Icon, BellIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import styles from './ClientHeader.module.css';
import { useUserName } from "../../../services/user_info";
import { useLocation, useNavigate } from "react-router-dom";
import PushPopUp from "../../../views/client/popup/PushPopUp";
import { checkNotificationAPI, getNotificationAPI } from '../../../services/notification';
import { NotificationResponseData } from "../../../types/notification";
import { handleLogout } from "../../../services/base";
import Input from "../../common/input/Input";
import Button from "../../common/button/Button";

interface HeaderProps {
    username?: string;
    className?: string;
    toggleSidebar?: () => void;
}

interface SearchKeyword {
    keyword: string;
    route: string;
    description?: string;
}

// 검색 키워드 설정 (상수로 분리)
const searchKeywords: SearchKeyword[] = [
    { keyword: '문서', route: '/client/documents', description: '문서 관리' },
    { keyword: '보고서', route: '/client/documents', description: '문서 관리' },
    { keyword: '견적서', route: '/client/documents', description: '문서 관리' },
    { keyword: '가공가이드라인', route: '/client/documents', description: '문서 관리' },
    { keyword: '프로젝트', route: '/client/projects', description: '프로젝트 관리' },
    { keyword: '프로젝트 추가 요청', route: '/client/projects', description: '프로젝트 관리' },
    { keyword: '결제', route: '/client/billing', description: '결제 관리' },
    { keyword: '신용카드', route: '/client/billing', description: '결제 관리' },
    { keyword: '결제방법', route: '/client/billing', description: '결제 관리' },
    { keyword: '작업자 추가', route: '/client/billing', description: '결제 관리' },
    { keyword: '계좌이체', route: '/client/billing', description: '결제 관리' },
    { keyword: '세금계산서 요청', route: '/client/billing', description: '결제 관리' },
    { keyword: '현금영수증 발행', route: '/client/billing', description: '결제 관리' },
    { keyword: '설정', route: '/client/account', description: '계정 설정' },
    { keyword: '비밀번호', route: '/client/account', description: '계정 설정' },
    { keyword: '이메일', route: '/client/account', description: '계정 설정' },
    { keyword: 'email', route: '/client/account', description: '계정 설정' },
    { keyword: '담당자 변경', route: '/client/account', description: '계정 설정' },
    { keyword: '업태 수정', route: '/client/account', description: '계정 설정' },
    { keyword: '종목 수정', route: '/client/account', description: '계정 설정' },
    { keyword: '사업장 소재지 수정', route: '/client/account', description: '계정 설정' },
    { keyword: '개인정보 선택항목 수집·이용 동의 여부', route: '/client/account', description: '계정 설정' },
    { keyword: '계정', route: '/client/account', description: '계정 설정' },
    { keyword: '사업자등록증', route: '/client/account', description: '계정 설정' },
];

// 주소별 헤더 출력 제목 (상수로 분리)
const pathTitles: Record<string, { title: string; exact?: boolean }> = {
    '/client': { title: 'Hello, ', exact: true },
    '/client/projects': { title: 'Projects' },
    '/client/documents': { title: 'Documents' },
    '/client/billing': { title: 'Billing' },
    '/client/account': { title: 'Account Settings' }
} as const;

const NOTIFICATION_REFRESH_INTERVAL = 60000; // 1분

// 클라이언트 헤더
const ClientHeader: React.FC<HeaderProps> = ({
                                                 className = '',
                                                 toggleSidebar
                                             }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const userName = useUserName();
    const searchRef = useRef<HTMLDivElement>(null);

    const [showNotifications, setShowNotifications] = useState(false);
    const [notifications, setNotifications] = useState<NotificationResponseData[]>([]);
    const [notificationCount, setNotificationCount] = useState(0);
    const [searchMode, setSearchMode] = useState(false);
    const [input, setInput] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
    const [isLoading, setIsLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // 검색 제안어 필터링
    const suggestions = useMemo(() => {
        if (!input.trim()) return [];

        const searchTerm = input.toLowerCase().trim();
        const filtered = searchKeywords.filter(item =>
            item.keyword.toLowerCase().includes(searchTerm)
        );

        // 중복 제거 (같은 route에 대해서는 하나의 항목만 표시)
        const uniqueByRoute = filtered.reduce((acc, current) => {
            const existing = acc.find(item => item.route === current.route);
            if (!existing) {
                acc.push(current);
            }
            return acc;
        }, [] as SearchKeyword[]);

        return uniqueByRoute.slice(0, 5); // 최대 5개까지 표시
    }, [input]);

    // 입력 변경 핸들러
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInput(value);
        setShowSuggestions(value.trim().length > 0);
        setSelectedSuggestionIndex(-1);
    }, []);

    // 키보드 이벤트 핸들러
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showSuggestions || suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedSuggestionIndex(prev =>
                    prev < suggestions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedSuggestionIndex(prev =>
                    prev > 0 ? prev - 1 : suggestions.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedSuggestionIndex >= 0) {
                    handleSuggestionClick(suggestions[selectedSuggestionIndex]);
                } else {
                    search();
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setSelectedSuggestionIndex(-1);
                break;
        }
    }, [showSuggestions, suggestions, selectedSuggestionIndex]);

    // 알림 가져오기
    const fetchNotifications = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await getNotificationAPI();
            setNotifications(data);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // 읽지 않은 알림 개수 확인
    const checkNotifications = useCallback(async () => {
        try {
            const count = await checkNotificationAPI();
            setNotificationCount(count);
        } catch (error) {
            console.error('Failed to check notifications:', error);
        }
    }, []);

    // 알림 새로고침
    const refreshNotifications = useCallback(async () => {
        await Promise.all([fetchNotifications(), checkNotifications()]);
    }, [fetchNotifications, checkNotifications]);

    // 검색 처리
    const search = useCallback(() => {
        const searchTerm = input.toLowerCase().trim();

        // 직접 키워드 매칭
        const matchedKeyword = searchKeywords.find(item =>
            item.keyword.toLowerCase().includes(searchTerm)
        );

        if (matchedKeyword) {
            navigate(matchedKeyword.route);
        }

        setSearchMode(false);
        setInput('');
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
    }, [input, navigate]);

    // 제안어 클릭 핸들러
    const handleSuggestionClick = useCallback((suggestion: SearchKeyword) => {
        navigate(suggestion.route);
        setSearchMode(false);
        setInput('');
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
    }, [navigate]);

    // 폼 제출 핸들러
    const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && suggestions.length > 0) {
            handleSuggestionClick(suggestions[selectedSuggestionIndex]);
        } else {
            search();
        }
    }, [search, selectedSuggestionIndex, suggestions, handleSuggestionClick]);

    // 검색 모드 토글
    const toggleSearchMode = useCallback(() => {
        setSearchMode(prev => !prev);
        if (searchMode) {
            setInput('');
            setShowSuggestions(false);
            setSelectedSuggestionIndex(-1);
        }
    }, [searchMode]);

    // 알림 팝업 닫기 핸들러
    const handleNotificationClose = useCallback(() => {
        setShowNotifications(false);
        checkNotifications();
    }, [checkNotifications]);

    // 알림 업데이트 핸들러
    const handleNotificationUpdate = useCallback(() => {
        refreshNotifications();
    }, [refreshNotifications]);

    // 화면 크기 감지
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 800);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // 외부 클릭 감지로 제안어 숨기기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
                setSelectedSuggestionIndex(-1);
            }
        };

        if (showSuggestions) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showSuggestions]);

    // 초기 로드 및 주기적 새로고침
    useEffect(() => {
        refreshNotifications();

        const interval = setInterval(refreshNotifications, NOTIFICATION_REFRESH_INTERVAL);
        return () => clearInterval(interval);
    }, [refreshNotifications]);

    // 페이지별 헤더 제목 계산
    const headerTitle = useMemo(() => {
        // 정확한 경로 매치 확인
        const exactMatch = Object.entries(pathTitles).find(([path, config]) =>
            config.exact && location.pathname === path
        );
        if (exactMatch) return exactMatch[1].title;

        // 시작 경로 매치 확인
        const startMatch = Object.entries(pathTitles).find(([path, config]) =>
            !config.exact && location.pathname.startsWith(path)
        );
        return startMatch ? startMatch[1].title : 'Dashboard';
    }, [location.pathname]);

    // 알림 배지 텍스트 계산
    const notificationBadgeText = useMemo(() => {
        return notificationCount > 99 ? '99+' : notificationCount.toString();
    }, [notificationCount]);

    return (
        <>
            <header className={`${styles.header} ${className}`}>
                {/* 헤더 제목 */}
                <div className={styles.titleSection}>
                    {searchMode && isMobile ? (
                        <h1/>
                    ) : location.pathname === '/client' ? (
                        <h1 className={styles.greeting}>
                            {headerTitle}{userName}
                        </h1>
                    ) : (
                        <h1>{headerTitle}</h1>
                    )}
                </div>

                <div className={`${styles.actions} ${searchMode ? styles.searchActive : ''}`}>
                    {/* 검색 버튼 */}
                    <button
                        className={styles.iconButton}
                        onClick={toggleSearchMode}
                        aria-label="검색"
                    >
                        <MagnifyingGlassIcon className={styles.icon}/>
                    </button>

                    {/* 검색창 */}
                    {searchMode && (
                        <div className={styles.searchContainer} ref={searchRef}>
                            <form onSubmit={handleSubmit} className={styles.searchForm}>
                                <Input
                                    value={input}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    placeholder="검색어를 입력하세요"
                                    fullWidth
                                    rightButton={
                                        <Button size="small" type="submit">
                                            검색
                                        </Button>
                                    }
                                />
                            </form>

                            {/* 검색 제안어 */}
                            {showSuggestions && suggestions.length > 0 && (
                                <div className={styles.suggestionsDropdown}>
                                    {suggestions.map((suggestion, index) => (
                                        <div
                                            key={`${suggestion.route}-${suggestion.keyword}`}
                                            className={`${styles.suggestionItem} ${
                                                index === selectedSuggestionIndex ? styles.selected : ''
                                            }`}
                                            onClick={() => handleSuggestionClick(suggestion)}
                                            onMouseEnter={() => setSelectedSuggestionIndex(index)}
                                        >
                                            <div className={styles.suggestionKeyword}>
                                                {suggestion.keyword}
                                            </div>
                                            {suggestion.description && (
                                                <div className={styles.suggestionDescription}>
                                                    {suggestion.description}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className={styles.notificationWrapper}>
                        {/* 푸시 알림 팝업 버튼 */}
                        <button
                            className={`${styles.iconButton} ${searchMode ? styles.searchActivatedButtons : ''}`}
                            onClick={() => setShowNotifications(true)}
                            disabled={isLoading}
                            aria-label={`알림 ${notificationCount}개`}
                        >
                            <BellIcon className={styles.icon}/>
                        </button>
                        {/* 안읽은 푸시 알림 개수 */}
                        {notificationCount > 0 && (
                            <span className={`${styles.notificationBadge} ${searchMode ? styles.searchActivatedButtons : ''}`}>
                                {notificationBadgeText}
                            </span>
                        )}
                    </div>

                    {/* 로그아웃 버튼 */}
                    <button
                        className={`${styles.iconButton} ${searchMode ? styles.searchActivatedButtons : ''}`}
                        onClick={handleLogout}
                        aria-label="로그아웃"
                    >
                        <ArrowRightStartOnRectangleIcon className={styles.icon}/>
                    </button>

                    {/* 모바일 메뉴 버튼 */}
                    <button
                        className={`${styles.mobileButton} ${searchMode ? styles.searchActivatedButtons : ''}`}
                        onClick={toggleSidebar}
                        aria-label="메뉴 토글"
                    >
                        <Bars4Icon className={styles.icon}/>
                    </button>
                </div>
            </header>

            {/* 푸시 알림 팝업 */}
            {showNotifications && (
                <PushPopUp
                    notifications={notifications}
                    onClose={handleNotificationClose}
                    onUpdate={handleNotificationUpdate}
                />
            )}
        </>
    );
};

export default ClientHeader;